import {Container, ContainerModule, type interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IUserStore, UserStore} from "data/stores/user/user.store";
import {AuthController, type IAuthController} from "views/controllers/auth/auth.controller";
import {
	type IUpdateAccountController,
	UpdateAccountController,
} from "views/pages/account/update_account/update_account.controller";
import {type ILoginController, LoginController} from "views/pages/login/login.controller";
import {API_URL, CONTENT_JSON_URL, JSON_URL} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";
import {CountriesStore, type ICountriesStore} from "data/stores/countries/countries.store";
import {RoundsStore, type IRoundsStore} from "data/stores/rounds/rounds.store";
import {PlayersStore, type IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, type ISquadsStore} from "data/stores/squads/squads.store";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {HelpController, type IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {
	type ILiveScoreController,
	LiveScoreController,
} from "views/components/live_score/live_score.controller";
import {ChecksumStore, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {
	type IRegistrationController,
	RegistrationController,
} from "views/pages/registration/registration.controller";
import {type IUserApiProvider, UserApiProvider} from "data/providers/api/user.api.provider";
import {
	type IPasswordApiProvider,
	PasswordApiProvider,
} from "data/providers/api/password.api.provider";
import {
	ForgotPasswordController,
	type IForgotPasswordController,
} from "views/pages/forgot_password/forgot_password.controller";
import {
	type IResetPasswordController,
	ResetPasswordController,
} from "views/pages/reset_password/reset_password.controller";
import {
	type ISessionController,
	SessionController,
} from "views/components/session/session.controller";
import {
	BootstrapController,
	type IBootstrapController,
} from "views/components/bootstrap/bootstrap.controller";
import {
	type ILeaguesApiProvider,
	LeaguesApiProvider,
} from "data/providers/api/leagues.api.provider";
import {type ILeaguesStore, LeaguesStore} from "data/stores/leagues/leagues.store";
import {
	type IMyLeaguesController,
	MyLeaguesController,
} from "views/pages/my_leagues/my_leagues.controller";
import {
	type IJoinLeaguesController,
	JoinLeaguesController,
} from "views/pages/join_leagues/join_leagues.controller";
import {
	type ICreateLeagueController,
	CreateLeagueController,
} from "views/pages/create_league/create_league.controller";
import {type ILeagueController, LeagueController} from "views/pages/league/league.controller";
import {
	type ILeagueInviteController,
	LeagueInviteController,
} from "views/pages/league_invite/league_invite.controller";
import {
	type ILeagueSettingsController,
	LeagueSettingsController,
} from "views/pages/league_settings/league_settings.controller";
import {
	type ILeagueAboutController,
	LeagueAboutController,
} from "views/pages/league_about/league_about.controller";
import {
	type ILeagueInviteFormController,
	LeagueInviteFormController,
} from "views/components/league_invite_form/league_invite_form.controller";
import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {
	type IModalErrorController,
	ModalErrorController,
} from "views/components/modals/modal_error/modal_error.controller";
import {
	type IModalConfirmController,
	ModalConfirmController,
} from "views/components/modals/modal_confirm/modal_confirm.controller";
import {
	type ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";
import {type IHeaderController, HeaderController} from "views/components/header/header.controller";
import {type IFooterController, FooterController} from "views/components/footer/footer.controller";
import {type IMenuStore, MenuStore} from "data/stores/menu/menu.store";
import {type IGigyaGateStore, GigyaGateStore} from "data/stores/gigya_gate/gigya_gate.store";
import {
	type IGigyaGateController,
	GigyaGateController,
} from "views/components/gigya_gate/gigya_gate.controller";
import {
	type IDashboardController,
	DashboardController,
} from "views/pages/dashboard/dashboard.controller";
import {type ILandingController, LandingController} from "views/pages/landing/landing.controller";
import {type IConferencesStore, ConferencesStore} from "data/stores/conferences/conferences.store";
import {type IDivisionsStore, DivisionsStore} from "data/stores/divisions/divisions.store";
import {type IStandingsStore, StandingsStore} from "data/stores/standings/standings.store";
import {type ITournamentsStore, TournamentsStore} from "data/stores/tournaments/tournaments.store";
import {
	type ILightPredictionsStore,
	LightPredictionsStore,
} from "data/stores/light_predictions/light_predictions.store";
import {
	type ILightPredictorController,
	LightPredictorController,
} from "views/components/light_predictor/light_predictor.controller";
import {
	type ILightPredictionSlotController,
	LightPredictionSlotController,
} from "views/components/light_predictor/light_predictor_predictions/light_prediction_slot/light_prediction_slot.controller";
import {
	type ILPDivisionSelectorController,
	LPDivisionSelectorController,
} from "views/components/light_predictor/l_p_division_selector/l_p_division_selector.controller";
import {
	CommunicationAccountController,
	type ICommunicationAccountController,
} from "views/pages/account/communication_account/communication_account.controller";
import {
	type INflAccountController,
	NflAccountController,
} from "views/pages/account/nfl_account/nfl_account.controller";
import {
	AccountController,
	type IAccountController,
} from "views/components/account/account_wrapper.controller";
import {
	type ILPWildCardController,
	LPWildCardController,
} from "views/components/light_predictor/l_p_wild_card/l_p_wild_card.controller";
import {
	type ISaveBarController,
	SaveBarController,
} from "views/components/save_bar/save_bar.controller";
import {
	type IModalSavePicksController,
	ModalSavePicksController,
} from "views/components/modals/modal_save_picks/modal_save_picks.controller";
import {
	type IModalLosePicksController,
	ModalLosePicksController,
} from "views/components/modals/modal_lose_picks/modal_lose_picks.controller";
import {
	LeavePicksPageModalController,
	type ILeavePicksPageModalController,
} from "views/components/modals/modal_leave_picks_page/modal_leave_picks_page.controller";
import {
	type IPredictionApiProvider,
	PredictionApiProvider,
} from "data/providers/api/prediction.api.provider";
import {
	type ILPSavedCardController,
	LPSavedCardController,
} from "views/components/light_predictor/l_p_saved_card/l_p_saved_card.controller";
import {
	type IModalSharePicksController,
	ModalSharePicksController,
} from "views/components/modals/modal_share_picks/modal_share_picks.controller";
import {
	type ILightPredictorPageController,
	LightPredictorPageController,
} from "views/pages/light_predictor/light_predictor.controller";
import {
	type IHeavyPredictorPageController,
	HeavyPredictorPageController,
} from "views/pages/heavy_predictor_page/heavy_predictor_page.controller";
import {
	type IModalWelcomeLightPredictorController,
	ModalWelcomeLightPredictorController,
} from "views/components/modals/modal_welcome/modal_welcome.controller";
import {
	type ITutorialController,
	TutorialController,
} from "views/components/tutorial/tutorial.controller";
import {type ITutorialStore, TutorialStore} from "data/stores/tutorial/tutorial.store";
import {
	type ITutorialStepController,
	TutorialStepController,
} from "views/components/tutorial/tutorial_step/tutorial_step.controller";
import {
	type ITutorialPaginationController,
	TutorialPaginationController,
} from "views/components/tutorial/tutorial_pagination/tutorial_pagination.controller";
import {type IShareStore, ShareStore} from "data/stores/share/share.store";
import {type IShareController, ShareController} from "views/controllers/share/share.controller";
import {
	type IHeavyPredictionsStore,
	HeavyPredictionsStore,
} from "data/stores/heavy_predictions/heavy_predictions.store";
import {
	type IRoundNavigationController,
	RoundNavigationController,
} from "views/components/round_navigation/round_navigation.controller";
import {
	type IWeeklyMatchesController,
	WeeklyMatchesController,
} from "views/components/weekly_matches/weekly_matches.controller";
import {type IMatchController, MatchController} from "views/components/match/match.controller";
import {
	type IStandingTableRowController,
	StandingTableRowController,
} from "views/components/standings_table/standing_table_row/standing_table_row.controller";
import {
	type ILightPredictorSliderController,
	LightPredictorSliderController,
} from "views/components/light_predictor/light_predictor_slider/light_predictor_slider.controller";
import {
	type ILPSliderCardController,
	LPSliderCardController,
} from "views/components/light_predictor/l_p_slider_card/l_p_slider_card.controller";
import {
	type ILPSliderCardOrderController,
	LPSliderCardOrderController,
} from "views/components/light_predictor/l_p_slider_card_order/l_p_slider_card_order.controller";
import {
	type ILightPredictorPredictionsController,
	LightPredictorPredictionsController,
} from "views/components/light_predictor/light_predictor_predictions/light_predictor_predictions.controller";
import {
	type IMatchFilterStore,
	MatchFilterStore,
} from "data/stores/match_filter_store/match_filter_store.store";
import {
	type IMatchFilterController,
	MatchFilterController,
} from "views/components/match_filter/match_filter.controller";
import {
	type IWelcomeModalStore,
	WelcomeModalStore,
} from "data/stores/welcome_modal/welcome_modal.store";
import {
	type IControllBarController,
	ControllBarController,
} from "views/components/controll_bar/controll_bar.controller";
import {
	type IHPPlayoffStandingsController,
	HPPlayoffStandingsController,
} from "views/components/h_p_playoff_standings/h_p_playoff_standings.controller";
import {
	type ICompareTableRowController,
	CompareTableRowController,
} from "views/components/standings_table/compare_table_row/compare_table_row.controller";
import {
	type IModalPlayoffFinalController,
	ModalPlayoffFinalController,
} from "views/components/modals/modal_playoff_final/modal_playoff_final.controller";
import {
	type ISecondStepSsoRouteController,
	SecondStepSsoRouteController,
} from "views/components/route/second_step_sso_route/second_step_sso_route.controller";
import {
	type ILanguageSelectController,
	LanguageSelectController,
} from "views/components/header/language_select/language_select.controller";
import {
	type IRoundNavItemController,
	RoundNavItemController,
} from "views/components/round_navigation/round_navigation_item/round_nav_item.controller";
import {
	GeoBlockingController,
	type IGeoBlockingController,
} from "views/components/route/geo_blocking_route/geo_blocking.controller";
import {CommonApiProvider, type ICommonApiProvider} from "data/providers/api/common.api.provider";
import {
	type IOrderedStandingsStore,
	OrderedStandingsStore,
} from "data/stores/ordered_standings/ordered_standings.store";
import {
	type IRankingApiProvider,
	RankingApiProvider,
} from "data/providers/api/ranking.api.provider";
import {
	type IOffSeasonController,
	OffSeasonController,
} from "views/pages/off_season/off_season.controller";
import {type IRankingStore, RankingStore} from "data/stores/ranking/ranking.store";
import {
	type IUserScoringController,
	UserScoringController,
} from "views/components/user_scoring/user_scoring.controller";
import {
	type IScoringKeysController,
	ScoringKeysController,
} from "views/components/scoring_keys/scoring_keys.controller";
import {
	type IModalOffSeasonController,
	ModalOffSeasonController,
} from "views/components/modals/modal_off_season/modal_off_season.controller";
import {
	type IEndSeasonController,
	EndSeasonController,
} from "views/components/route/end_season/end_season.controller";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IUserApiProvider>(Bindings.UserApiProvider).to(UserApiProvider);
	bind<IPasswordApiProvider>(Bindings.PasswordApiProvider).to(PasswordApiProvider);
	bind<ILeaguesApiProvider>(Bindings.LeaguesApiProvider).to(LeaguesApiProvider);
	bind<IPredictionApiProvider>(Bindings.PredictionApiProvider).to(PredictionApiProvider);
	bind<ICommonApiProvider>(Bindings.CommonApiProvider).to(CommonApiProvider);
	bind<IRankingApiProvider>(Bindings.RankingApiProvider).to(RankingApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();

	bind<ICountriesStore>(Bindings.CountriesStore).to(CountriesStore);
	bind<IStaticContentStore>(Bindings.StaticContentStore).to(StaticContentStore);

	bind<IChecksumStore>(Bindings.ChecksumStore).to(ChecksumStore);
	bind<ILeaguesStore>(Bindings.LeaguesStore).to(LeaguesStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<IMenuStore>(Bindings.MenuStore).to(MenuStore).inSingletonScope();
	bind<IConferencesStore>(Bindings.ConferencesStore).to(ConferencesStore).inSingletonScope();
	bind<IDivisionsStore>(Bindings.DivisionsStore).to(DivisionsStore).inSingletonScope();
	bind<IStandingsStore>(Bindings.StandingsStore).to(StandingsStore).inSingletonScope();
	bind<ITournamentsStore>(Bindings.TournamentsStore).to(TournamentsStore).inSingletonScope();
	bind<ILightPredictionsStore>(Bindings.LightPredictionsStore)
		.to(LightPredictionsStore)
		.inSingletonScope();
	bind<IShareStore>(Bindings.ShareStore).to(ShareStore).inSingletonScope();
	bind<ITutorialStore>(Bindings.TutorialStore).to(TutorialStore).inSingletonScope();
	bind<IHeavyPredictionsStore>(Bindings.HeavyPredictionsStore)
		.to(HeavyPredictionsStore)
		.inSingletonScope();
	bind<IMatchFilterStore>(Bindings.MatchFilterStore).to(MatchFilterStore).inSingletonScope();
	bind<IWelcomeModalStore>(Bindings.WelcomeModalStore).to(WelcomeModalStore).inSingletonScope();
	bind<IGigyaGateStore>(Bindings.GigyaGateStore).to(GigyaGateStore).inSingletonScope();
	bind<IOrderedStandingsStore>(Bindings.OrderedStandingsStore)
		.to(OrderedStandingsStore)
		.inSingletonScope();
	bind<IRankingStore>(Bindings.RankingStore).to(RankingStore).inSingletonScope();
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<ILoginController>(Bindings.LoginController).to(LoginController);
	bind<IForgotPasswordController>(Bindings.ForgotPasswordController).to(ForgotPasswordController);
	bind<IResetPasswordController>(Bindings.ResetPasswordController).to(ResetPasswordController);
	bind<IRegistrationController>(Bindings.RegistrationController).to(RegistrationController);
	bind<IUpdateAccountController>(Bindings.UpdateAccountController).to(UpdateAccountController);
	bind<ICommunicationAccountController>(Bindings.CommunicationAccountController).to(
		CommunicationAccountController
	);
	bind<INflAccountController>(Bindings.NflAccountController).to(NflAccountController);
	bind<IAccountController>(Bindings.AccountController).to(AccountController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);
	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<ILiveScoreController>(Bindings.LiveScoreController).to(LiveScoreController);
	bind<ISessionController>(Bindings.SessionController).to(SessionController);
	bind<IBootstrapController>(Bindings.BootstrapController).to(BootstrapController);
	bind<IMyLeaguesController>(Bindings.MyLeaguesController).to(MyLeaguesController);
	bind<IJoinLeaguesController>(Bindings.JoinLeaguesController).to(JoinLeaguesController);
	bind<ICreateLeagueController>(Bindings.CreateLeagueController).to(CreateLeagueController);
	bind<ILeagueController>(Bindings.LeagueController).to(LeagueController);
	bind<ILeagueInviteController>(Bindings.LeagueInviteController).to(LeagueInviteController);
	bind<ILeagueSettingsController>(Bindings.LeagueSettingsController).to(LeagueSettingsController);
	bind<ILeagueAboutController>(Bindings.LeagueAboutController).to(LeagueAboutController);
	bind<ILeagueInviteFormController>(Bindings.LeagueInviteFormController).to(
		LeagueInviteFormController
	);
	bind<IModalErrorController>(Bindings.ModalErrorController).to(ModalErrorController);
	bind<IModalConfirmController>(Bindings.ModalConfirmController).to(ModalConfirmController);
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IFooterController>(Bindings.FooterController).to(FooterController);
	bind<IGigyaGateController>(Bindings.GigyaGateController).to(GigyaGateController);
	bind<IDashboardController>(Bindings.DashboardController).to(DashboardController);
	bind<ILandingController>(Bindings.LandingController).to(LandingController);
	bind<ILightPredictorController>(Bindings.LightPredictorController).to(LightPredictorController);
	bind<ILightPredictionSlotController>(Bindings.LightPredictionSlotController).to(
		LightPredictionSlotController
	);
	bind<ILPDivisionSelectorController>(Bindings.LPDivisionSelectorController).to(
		LPDivisionSelectorController
	);
	bind<ILPWildCardController>(Bindings.LPWildCardController).to(LPWildCardController);
	bind<ISaveBarController>(Bindings.SaveBarController).to(SaveBarController);
	bind<IModalSavePicksController>(Bindings.ModalSavePicksController).to(ModalSavePicksController);
	bind<IModalLosePicksController>(Bindings.ModalLosePicksController).to(ModalLosePicksController);
	bind<ILeavePicksPageModalController>(Bindings.LeavePicksPageModal).to(
		LeavePicksPageModalController
	);
	bind<ILPSavedCardController>(Bindings.LPSavedCardController).to(LPSavedCardController);
	bind<IModalSharePicksController>(Bindings.ModalSharePicksController).to(
		ModalSharePicksController
	);
	bind<ILightPredictorPageController>(Bindings.LightPredictorPageController).to(
		LightPredictorPageController
	);
	bind<IHeavyPredictorPageController>(Bindings.HeavyPredictorPageController).to(
		HeavyPredictorPageController
	);
	bind<ILightPredictorSliderController>(Bindings.LightPredictorSliderController).to(
		LightPredictorSliderController
	);
	bind<ILPSliderCardController>(Bindings.LPSliderCardController).to(LPSliderCardController);
	bind<ILPSliderCardOrderController>(Bindings.LPSliderCardOrderController).to(
		LPSliderCardOrderController
	);
	bind<IModalWelcomeLightPredictorController>(Bindings.ModalWelcomeLightPredictorController).to(
		ModalWelcomeLightPredictorController
	);
	bind<IShareController>(Bindings.ShareController).to(ShareController);
	bind<ITutorialController>(Bindings.TutorialController).to(TutorialController);
	bind<ITutorialStepController>(Bindings.TutorialStepController).to(TutorialStepController);
	bind<ITutorialPaginationController>(Bindings.TutorialPaginationController).to(
		TutorialPaginationController
	);
	bind<ILightPredictorPredictionsController>(Bindings.LightPredictorPredictionsController).to(
		LightPredictorPredictionsController
	);
	bind<IRoundNavigationController>(Bindings.RoundNavigationController).to(
		RoundNavigationController
	);
	bind<IRoundNavItemController>(Bindings.RoundNavItemController).to(RoundNavItemController);
	bind<IWeeklyMatchesController>(Bindings.WeeklyMatchesController).to(WeeklyMatchesController);
	bind<IMatchController>(Bindings.MatchController).to(MatchController);
	bind<IStandingTableRowController>(Bindings.StandingTableRowController).to(
		StandingTableRowController
	);
	bind<IMatchFilterController>(Bindings.MatchFilterController).to(MatchFilterController);
	bind<IControllBarController>(Bindings.ControllBarController).to(ControllBarController);
	bind<IHPPlayoffStandingsController>(Bindings.HPPlayoffStandingsController).to(
		HPPlayoffStandingsController
	);
	bind<ICompareTableRowController>(Bindings.CompareTableRowController).to(
		CompareTableRowController
	);
	bind<IModalPlayoffFinalController>(Bindings.ModalPlayoffFinalController).to(
		ModalPlayoffFinalController
	);
	bind<ISecondStepSsoRouteController>(Bindings.SecondStepSsoRouteController).to(
		SecondStepSsoRouteController
	);
	bind<ILanguageSelectController>(Bindings.LanguageSelectController).to(LanguageSelectController);
	bind<IGeoBlockingController>(Bindings.GeoBlockingController).to(GeoBlockingController);
	bind<IOffSeasonController>(Bindings.OffSeasonController).to(OffSeasonController);
	bind<IUserScoringController>(Bindings.UserScoringController).to(UserScoringController);
	bind<IScoringKeysController>(Bindings.ScoringKeysController).to(ScoringKeysController);
	bind<IModalOffSeasonController>(Bindings.ModalOffSeasonController).to(ModalOffSeasonController);
	bind<IEndSeasonController>(Bindings.EndSeasonController).to(EndSeasonController);
});
